import { useModuleBEM } from "@b2bportal/core-themes";
import { Funnel } from "@b2bportal/core-types";
import { EligibleProduct } from "@b2bportal/wallet-offer-api";
import { useI18nContext } from "@hopper-b2b/i18n";

import { WalletOfferCard } from "./WalletOfferCard";
import styles from "./WalletOffersSection.module.scss";

import { Experiments, useExperiment } from "@/modules/experiments/src";
import { useWalletOffers } from "@/utilities";

const FUNNEL_ELIGIBLE_PRODUCT: Partial<Record<Funnel, EligibleProduct>> = {
  [Funnel.Lodging]: EligibleProduct.Lodging,
  [Funnel.Air]: EligibleProduct.Air,
  [Funnel.Ground]: EligibleProduct.Ground,
};

/**
 * TODO(rbc-travel-wallet): The design shows a destination as each card's
 * heading, but the WalletOffer contract has no destination field. Until the BE
 * adds one, cards are labelled from this hardcoded placeholder list (by index).
 * Replace with the offer's destination field once available.
 */
export const PLACEHOLDER_DESTINATIONS = [
  "Italy",
  "Lake Tahoe",
  "Grand Canyon West",
  "Los Angeles",
  "Washington D.C.",
  "Toronto",
  "Las Vegas",
  "Chile",
];

const CTA_ONE_LINE_CHAR_LIMIT = 12;

export const WalletOffersSection = ({ funnel }: { funnel: Funnel }) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "WalletOffersSection");
  const { isExperimentVariant } = useExperiment();

  const walletOffersEnabled = isExperimentVariant(
    Experiments.WALLET_OFFERS.name,
    Experiments.WALLET_OFFERS.variants.available
  );

  const product = FUNNEL_ELIGIBLE_PRODUCT[funnel];
  const { getOffersForProduct } = useWalletOffers({
    skip: !walletOffersEnabled || product == null,
  });

  const offers = product == null ? [] : getOffersForProduct(product);

  // Long CTA copy can't fit one line in a two-up card on narrow screens, so
  // switch the grid to a single column below lg.
  const hasLongCta =
    t("walletOffersSection.cta").length > CTA_ONE_LINE_CHAR_LIMIT;

  if (!walletOffersEnabled || product == null || offers.length === 0) {
    return null;
  }

  return (
    <section className={block}>
      <div className={cn("content")}>
        <div className={cn("header")}>
          <h2 className={cn("title")}>{t("walletOffersSection.title")}</h2>
          <span className={cn("sponsored")}>
            {t("walletOffersSection.sponsored")}
          </span>
        </div>
        <div className={cn("grid", { "single-column": hasLongCta })}>
          {offers.map((offer, index) => (
            <WalletOfferCard
              key={offer.id}
              offer={offer}
              product={product}
              destination={
                PLACEHOLDER_DESTINATIONS[
                  index % PLACEHOLDER_DESTINATIONS.length
                ]
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
};
